/**
 * Theme: the multi-buy section on the product page: the offer line's jump link,
 * the category filter, and hiding the cards of products in the bag.
 *
 * WHAT THIS FILE DOES NOT DO: order anything, or work out a price. The cards
 * arrive from Liquid already in their final order, each tagged with the views it
 * belongs to; filtering only toggles `hidden`. Every price and saving is Liquid.
 *
 * Rail arrows and the scroll indicator are <core-rail> (assets/core-rail.js);
 * the cards' Add buttons open the global quick view (assets/core-quick-view.js);
 * the tracker and display prices follow the cart through assets/core-cart.js.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------------
     "SHOP THE OFFER", under the price. It ships visible, linking to every
     product (snippets/core-multibuy-tier.liquid), so it never appears late or
     moves the buy column. While the multi-buy section is on the page the click
     scrolls to it instead: smooth unless the shopper asks for reduced motion,
     landing below the sticky header through its scroll-margin-top.
     ------------------------------------------------------------------------ */
  function current() {
    return document.querySelector('[data-mb-root]');
  }

  function initJump() {
    document.querySelectorAll('[data-multibuy-jump]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        // Looked up on every click: the shell is replaced by the rail when it
        // lands, and the shell holds the rail's exact height, so a jump made
        // before it arrives is already in the right place when it does. With no
        // section (hidden after a failed or empty fetch), the link's own href
        // stands.
        var section = current();
        if (!section || section.closest('[hidden]')) return;
        event.preventDefault();
        section.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
        // A smooth scroll fixes its destination once. If anything above changes
        // height mid-journey, nudge once it has settled.
        window.setTimeout(function () {
          var header = document.querySelector('.core-header');
          var limit = header ? header.getBoundingClientRect().bottom : 0;
          var landed = current();
          if (!landed) return;
          var top = landed.getBoundingClientRect().top;
          if (top < limit || top > limit + 40) landed.scrollIntoView({ behavior: 'auto', block: 'start' });
        }, reduceMotion.matches ? 50 : 900);
      });
    });
  }

  /* ------------------------------------------------------------------------
     THE VIEWS, AND THE BAG. Every card carries its views (data-mb-views) and its
     product id (data-mb-pid), in the one order Liquid wrote. A view shows its
     first `cap` cards whose product is NOT in the bag (cap: data-mb-all for All,
     data-mb-cat for a category), so adding a product hides its card and the
     next ones move up, and removing it brings it back in its own place. Only
     `hidden` changes: nothing is re-fetched or re-ordered. A category with no
     card left out of the bag hides its button; if it was the selected one, the
     view goes back to All.

     The bag comes from assets/core-cart.js (`core:bag`, the product ids of the
     drawer it has just re-rendered, after any change from anywhere on the page),
     and from /cart.js when the rail lands and when the tab comes back into view
     (a change made in another tab). While the quick view is stepping through
     this rail the change waits until it closes, so its next and previous never
     skip a product mid-browse.
     ------------------------------------------------------------------------ */
  var bag = null; // product ids in the bag, as strings
  var bagStamp = 0;
  var pending = false;

  function idsOf(cart) {
    var ids = [];
    ((cart && cart.items) || []).forEach(function (item) {
      var id = String(item.product_id);
      if (ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function viewOf(section) {
    var on = section.querySelector('[data-mb-filter][aria-pressed="true"]');
    return on ? on.getAttribute('data-mb-filter') : 'all';
  }

  function inBag(card) {
    return !!bag && bag.indexOf(card.getAttribute('data-mb-pid')) !== -1;
  }

  function hasView(card, key) {
    return (' ' + (card.getAttribute('data-mb-views') || '') + ' ').indexOf(' ' + key + ' ') !== -1;
  }

  /* The card the shopper is looking at: the first one reaching into the rail's
     visible area, and where it sits. */
  function anchorOf(rail) {
    var left = rail.getBoundingClientRect().left;
    var cards = rail.querySelectorAll('[data-mb-views]:not([hidden])');
    for (var i = 0; i < cards.length; i++) {
      var r = cards[i].getBoundingClientRect();
      if (r.right > left + 1) return { card: cards[i], offset: r.left - left };
    }
    return null;
  }

  function show(section, opts) {
    var rail = section.querySelector('[data-mb-rail]');
    if (!rail) return;
    var cards = Array.prototype.slice.call(rail.querySelectorAll('[data-mb-views]'));
    var view = viewOf(section);

    // Buttons: a category stays while it has a card out of the bag.
    section.querySelectorAll('[data-mb-filter]').forEach(function (chip) {
      var key = chip.getAttribute('data-mb-filter');
      if (key === 'all') return;
      chip.hidden = !cards.some(function (card) {
        return hasView(card, key) && !inBag(card);
      });
    });
    var current = section.querySelector('[data-mb-filter="' + view + '"]');
    if (view !== 'all' && (!current || current.hidden)) {
      view = 'all';
      section.querySelectorAll('[data-mb-filter]').forEach(function (chip) {
        chip.setAttribute('aria-pressed', chip.getAttribute('data-mb-filter') === 'all' ? 'true' : 'false');
      });
      opts = { reset: true };
    }

    var anchor = opts && opts.reset ? null : anchorOf(rail);
    var focused = document.activeElement && rail.contains(document.activeElement) ? document.activeElement.closest('[data-mb-views]') : null;

    var cap = parseInt(section.getAttribute(view === 'all' ? 'data-mb-all' : 'data-mb-cat'), 10) || 40;
    var shown = 0;
    cards.forEach(function (card) {
      var on = hasView(card, view) && !inBag(card) && shown < cap;
      if (on) shown += 1;
      if (card.hidden === on) card.hidden = !on;
    });

    if (opts && opts.reset) {
      rail.scrollLeft = 0;
    } else if (anchor) {
      // Keep the shopper's place: the card they were looking at, or the next one
      // still showing if it was the one that left, stays where it was.
      var keep = anchor.card;
      if (keep.hidden) {
        var i = cards.indexOf(keep);
        keep = null;
        for (var n = i + 1; n < cards.length && !keep; n++) if (!cards[n].hidden) keep = cards[n];
        for (var b = i - 1; b >= 0 && !keep; b--) if (!cards[b].hidden) keep = cards[b];
      }
      if (keep) {
        var moved = keep.getBoundingClientRect().left - rail.getBoundingClientRect().left - anchor.offset;
        if (Math.abs(moved) > 0.5) rail.scrollLeft += moved;
      }
    }

    // Focus on a card that just left moves to the next card showing.
    if (focused && focused.hidden) {
      var at = cards.indexOf(focused);
      var next = null;
      for (var f = at + 1; f < cards.length && !next; f++) if (!cards[f].hidden) next = cards[f];
      for (var g = at - 1; g >= 0 && !next; g--) if (!cards[g].hidden) next = cards[g];
      var target = next && next.querySelector('a, button');
      if (target) target.focus({ preventScroll: true });
    }
  }

  function browsing(section) {
    var dialog = document.querySelector('[data-qv-dialog]');
    var rail = section.querySelector('[data-mb-rail]');
    return !!(dialog && dialog.open && dialog.qvGroup && dialog.qvGroup === rail);
  }

  function setBag(ids) {
    bag = ids;
    bagStamp += 1;
    var section = current();
    if (!section || section.hasAttribute('data-mb-shell')) return;
    if (browsing(section)) {
      pending = true;
      return;
    }
    show(section);
  }

  function initFilter(section) {
    section.querySelectorAll('[data-mb-filter]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        section.querySelectorAll('[data-mb-filter]').forEach(function (c) {
          c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
        });
        show(section, { reset: true });
      });
    });
  }

  document.addEventListener('core:bag', function (event) {
    setBag((event.detail && event.detail.products ? event.detail.products : []).map(String));
  });

  function readBag() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(idsOf);
  }

  // Another tab may have changed the bag while this one was out of view.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'visible') return;
    var stamp = bagStamp;
    readBag()
      .then(function (ids) {
        if (stamp === bagStamp && (!bag || ids.join() !== bag.join())) setBag(ids);
      })
      .catch(function () {});
  });

  function bindDialog() {
    var dialog = document.querySelector('[data-qv-dialog]');
    if (!dialog || dialog.__mbBound) return;
    dialog.__mbBound = true;
    dialog.addEventListener('close', function () {
      if (!pending) return;
      pending = false;
      var section = current();
      if (section && !section.hasAttribute('data-mb-shell')) show(section);
    });
  }

  /* ------------------------------------------------------------------------
     THE RAIL, FETCHED. The page ships a shell (sections/core-multibuy.liquid,
     data-mb-shell) whose ghost cards hold the rail's real height. As soon as the
     page has parsed, this asks for the same section by id; that render is the
     full rail, identical to what the page would have rendered inline, and it
     replaces the shell in place. A failed request is retried once; a second
     failure, or a rail with no cards, hides the whole section, so no empty
     placeholder is ever left on the page ("Shop the offer" then keeps its own
     link to every product).
     ------------------------------------------------------------------------ */
  function hide(section) {
    var wrapper = section.closest('.shopify-section') || section;
    wrapper.hidden = true;
    wrapper.style.display = 'none';
  }

  function fetchRail(section, attempt) {
    var id = section.getAttribute('data-section-id');
    var url = window.location.pathname + '?section_id=' + encodeURIComponent(id);
    // The bag is read alongside the rail, so the in-bag cards are hidden before
    // the rail is put on the page: nothing appears and then leaves.
    var stamp = bagStamp;
    var bagRead = readBag().catch(function () {
      return null;
    });
    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        return bagRead.then(function (ids) {
          if (ids && stamp === bagStamp) bag = ids;
          return html;
        });
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var next = doc.querySelector('[data-mb-root]');
        if (!next || next.hasAttribute('data-mb-shell') || !next.querySelector('[data-mb-rail] [data-mb-views]')) {
          hide(section);
          return;
        }
        var live = document.importNode(next, true);
        initFilter(live);
        show(live, { reset: true });
        section.replaceWith(live);
        // The views were drawn while detached; a bag change that arrived in the
        // meantime is drawn now.
        show(live);
      })
      .catch(function () {
        if (attempt < 1) {
          window.setTimeout(function () {
            fetchRail(section, attempt + 1);
          }, 1500);
          return;
        }
        hide(section);
      });
  }

  function init() {
    var section = current();
    if (!section || section.__mbBound) return;
    section.__mbBound = true;
    initJump();
    bindDialog();
    if (section.hasAttribute('data-mb-shell')) {
      fetchRail(section, 0);
    } else {
      initFilter(section);
      readBag()
        .then(setBag)
        .catch(function () {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
